/**
 * Print-layout helpers aligned with blox-bi `app/page_layout.py`.
 * Live dashboard canvas ignores these; packs/export consume them.
 */

export type PagePurpose = 'screen' | 'a4_portrait' | 'a4_landscape' | 'ppt_16x9' | 'excel_grid';

export type PageFitMode = 'actual' | 'fit_width' | 'fit_one_page';
export type PageOrientation = 'portrait' | 'landscape';

/**
 * A page with no stored purpose is a normal on-screen dashboard page, not a
 * print sheet. Defaulting to `a4_landscape` meant every legacy page — and now
 * every newly created one, since the print-layout controls were removed from
 * the page modal — claimed to be an A4 sheet it had never been set up as.
 */
export const DEFAULT_PURPOSE: PagePurpose = 'screen';
export const DEFAULT_MARGIN_MM = 10;
export const DEFAULT_FIT_MODE: PageFitMode = 'fit_width';

export type PagePurposePreset = {
  label: string;
  description: string;
  page_size: string;
  orientation: PageOrientation;
};

export const PAGE_PURPOSE_PRESETS: Record<PagePurpose, PagePurposePreset> = {
  screen: {
    label: 'Screen',
    description: 'Interactive working view; not sized for print.',
    page_size: 'fluid',
    orientation: 'landscape',
  },
  a4_portrait: {
    label: 'A4 portrait',
    description: 'P&L and narrative pack pages.',
    page_size: 'A4',
    orientation: 'portrait',
  },
  a4_landscape: {
    label: 'A4 landscape',
    description: 'Wide variance and KPI strips.',
    page_size: 'A4',
    orientation: 'landscape',
  },
  ppt_16x9: {
    label: 'Widescreen 16:9',
    description: 'Board slides.',
    page_size: 'widescreen',
    orientation: 'landscape',
  },
  excel_grid: {
    label: 'Excel grid',
    description: 'Statement-shaped tables for spreadsheet export.',
    page_size: 'spreadsheet',
    orientation: 'landscape',
  },
};

const VALID_PURPOSES = new Set(Object.keys(PAGE_PURPOSE_PRESETS));
const VALID_FIT_MODES = new Set<PageFitMode>(['actual', 'fit_width', 'fit_one_page']);
const VALID_ORIENTATIONS = new Set<PageOrientation>(['portrait', 'landscape']);

export function normalizePurpose(purpose?: string | null): PagePurpose {
  if (purpose && VALID_PURPOSES.has(purpose)) {
    return purpose as PagePurpose;
  }
  return DEFAULT_PURPOSE;
}

/** Human-readable label for a stored purpose value. */
export function purposeLabel(purpose?: string | null): string {
  const key = normalizePurpose(purpose);
  return PAGE_PURPOSE_PRESETS[key].label;
}

/** Short badge text, e.g. "A4 landscape". */
export function purposeBadgeText(purpose?: string | null): string {
  const key = normalizePurpose(purpose);
  const preset = PAGE_PURPOSE_PRESETS[key];
  if (key === 'screen') {
    return preset.label;
  }
  if (key === 'ppt_16x9') {
    return preset.label;
  }
  if (key === 'excel_grid') {
    return preset.label;
  }
  return `${preset.page_size} ${preset.orientation}`;
}

export function normalizeFitMode(fitMode?: string | null): PageFitMode {
  if (fitMode && VALID_FIT_MODES.has(fitMode as PageFitMode)) {
    return fitMode as PageFitMode;
  }
  return DEFAULT_FIT_MODE;
}

export function purposeSelectOptions(): Array<{ value: PagePurpose; label: string }> {
  return (Object.keys(PAGE_PURPOSE_PRESETS) as PagePurpose[]).map((value) => ({
    value,
    label: PAGE_PURPOSE_PRESETS[value].label,
  }));
}

export function fitModeSelectOptions(): Array<{ value: PageFitMode; label: string }> {
  return [
    { value: 'fit_width', label: 'Fit to width' },
    { value: 'fit_one_page', label: 'Fit on one page' },
    { value: 'actual', label: 'Actual size' },
  ];
}

export function orientationSelectOptions(): Array<{ value: PageOrientation; label: string }> {
  return [
    { value: 'portrait', label: 'Portrait' },
    { value: 'landscape', label: 'Landscape' },
  ];
}

export type ResolvedPageLayout = {
  purpose: PagePurpose;
  page_size: string;
  orientation: PageOrientation;
  margin_mm: number;
  fit_mode: PageFitMode;
};

/** Fill missing print fields from the purpose preset (explicit values win). */
export function resolvePageLayout(fields: {
  purpose?: string | null;
  page_size?: string | null;
  orientation?: string | null;
  margin_mm?: number | null;
  fit_mode?: string | null;
}): ResolvedPageLayout {
  const purpose = normalizePurpose(fields.purpose);
  const preset = PAGE_PURPOSE_PRESETS[purpose];
  const orientation =
    fields.orientation && VALID_ORIENTATIONS.has(fields.orientation as PageOrientation)
      ? (fields.orientation as PageOrientation)
      : preset.orientation;
  let marginMm = fields.margin_mm == null ? DEFAULT_MARGIN_MM : Number(fields.margin_mm);
  if (!Number.isFinite(marginMm) || marginMm < 0) {
    marginMm = DEFAULT_MARGIN_MM;
  }
  return {
    purpose,
    page_size: fields.page_size || preset.page_size,
    orientation,
    margin_mm: marginMm,
    fit_mode: normalizeFitMode(fields.fit_mode),
  };
}

/** Whether orientation/margin/fit controls apply (non-screen purposes). */
export function showsPrintDetailFields(purpose?: string | null): boolean {
  return normalizePurpose(purpose) !== 'screen';
}
